import { type FC } from 'react'
import { useApolloClient, useMutation } from '@truepill/tpos-react-router'
import { AutobotType, LaunchDarkly, UserRoles, autobotMap } from '@truepill/tpos-types'
import { ReactComponent as LoadingDots } from 'assets/icons/loading_dots.svg'
import { ReactComponent as PlusIcon } from 'assets/icons/plus.svg'
import { ReactComponent as PrintIcon } from 'assets/icons/print.svg'
import { ReactComponent as ReverseIcon } from 'assets/icons/reverse.svg'
import { ReactComponent as RouteIcon } from 'assets/icons/route.svg'
import ActionButton from 'components/ActionButton'
import AuthLimited from 'components/AuthLimited'
import IconWrapper from 'components/IconWrapper'
import OrderSlaFilterDropdown from 'components/OrderSlaFilterDropdown'
import OrderSortDropdown from 'components/OrderSortDropdown'
import PageSizeDropDown from 'components/PageSizeDropdown'
import { ActionButtonContainer } from 'components/PageStructure'
import SendToAutomationButton from 'components/SendToAutomationButton'
import { BULK_SYNC_AUTOMATION_STATUS, BULK_SEND_TO_MANUAL, CREATE_COPAY_REQUEST } from 'gql'
import useErrorToast from 'hooks/toast/useErrorToast'
import useInfoToast from 'hooks/toast/useInfoToast'
import useSuccessToast from 'hooks/toast/useSuccessToast'
import useFulfillmentQueue, { FulfillmentQueueName } from 'hooks/useFulfillmentQueue'
import useGetNextAdjudication from 'hooks/useGetNextAdjudication'
import useGetNextOtcOnly from 'hooks/useGetNextOtcOnly'
import useGetNextPV1 from 'hooks/useGetNextPV1'
import useGetNextTriage from 'hooks/useGetNextTriage'
import useQueryParamBoolean from 'hooks/useQueryParamBoolean'
import CreateCopayRequestModal from 'modals/CreateCopayRequestModal'
import PrintModal from 'modals/PrintModal'
import PrintPickSlipModal from 'modals/PrintPickSlipModal'
import SendToManualModal from 'modals/SendToManualModal'
import { useFlag } from 'providers/LaunchDarklyProvider'
import { useModalContext } from 'providers/Overlays/ModalProvider'
import { useSelectionContext } from 'providers/SelectionProvider'
import { usePlusClient } from 'providers/VisionRouter'
import styled from 'styled-components'
import { bodyPrimaryColor } from 'styles/styleVariables'
import type { Fill, Order } from 'types'
import { isOrderLevelQueue } from 'utils'
import { StyledBasicActionButton } from '../PharmacyPage/PharmacyOrdersPage/StyledComponents'
import { WrapButton } from './orders/rx/screens/fill/ActionButtons'

const ActionButtons = (): JSX.Element => {
  const {
    currentLocation: { queryMap },
    QueryToolkit,
  } = usePlusClient()
  const { autobot, shelfLocations } = queryMap
  const { showModal } = useModalContext()
  const shouldSortByShelfLocation = useFlag(LaunchDarkly.FeatureFlags.TEMP_ENABLE_WAREHOUSE_SHELF_LOCATIONS_SORT)

  const { startPV1Work } = useGetNextPV1()
  const { startOtcOnlyWork } = useGetNextOtcOnly()
  const { startWork: startAdjudicationWork } = useGetNextAdjudication()
  const { startWork: startTriageWork } = useGetNextTriage()
  const fulfillmentQueue = useFulfillmentQueue()

  const { selections, initSelections } = useSelectionContext()
  const client = useApolloClient()

  const [, { loading: loadingCreateCopayRequest }] = useMutation(CREATE_COPAY_REQUEST)

  const [otcOnlyControl] = useQueryParamBoolean('otcOnly', false)
  const [otcRxOnlyControl] = useQueryParamBoolean('rxOtcOnly', false)

  const shelfLocationComparator = (a: string, b: string) => {
    const shelfA = a.split('-')
    const shelfB = b.split('-')

    // section in pharmacy
    if (shelfA[0] < shelfB[0]) return -1
    if (shelfA[0] > shelfB[0]) return 1

    // shelf
    if (shelfA[1] < shelfB[1]) return -1
    if (shelfA[1] > shelfB[1]) return 1

    // row
    if (shelfA[2] < shelfB[2]) return -1
    if (shelfA[2] > shelfB[2]) return 1

    // section on row
    if (shelfA[3] < shelfB[3]) return -1
    if (shelfA[3] > shelfB[3]) return 1

    return 0
  }

  let sorted = selections

  if (!isOrderLevelQueue(fulfillmentQueue?.name)) {
    if (shelfLocations?.length > 0 && shouldSortByShelfLocation) {
      // Group all fills from the same batch order together.
      const groupedByOrder = selections.reduce((acc, selection) => {
        const typedSelection = selection as { _id: string; fill: Fill; order: Order }
        const orderId = typedSelection.order?._id
        if (!orderId) return acc
        if (!acc[orderId]) {
          acc[orderId] = []
        }
        acc[orderId].push(typedSelection)
        return acc
      }, {} as Record<string, { _id: string; fill: Fill; order: Order }[]>)

      // Sort fills within each batch order by shelf location.
      const sortedGroups = Object.values(groupedByOrder).map(group => {
        return group.sort((a, b) => {
          const warehouseLocationA = a.fill?.dispensed?.ffsInventoryInfo?.warehouseLocation
          const warehouseLocationB = b.fill?.dispensed?.ffsInventoryInfo?.warehouseLocation
          if (!warehouseLocationA) return 1
          if (!warehouseLocationB) return -1
          return shelfLocationComparator(warehouseLocationA, warehouseLocationB)
        })
      })

      // Sort the batches based on the shelf location of their first item.
      sortedGroups.sort((groupA, groupB) => {
        const firstItemA = groupA[0]
        const firstItemB = groupB[0]
        const warehouseLocationA = firstItemA.fill?.dispensed?.ffsInventoryInfo?.warehouseLocation
        const warehouseLocationB = firstItemB.fill?.dispensed?.ffsInventoryInfo?.warehouseLocation
        if (!warehouseLocationA) return 1
        if (!warehouseLocationB) return -1
        return shelfLocationComparator(warehouseLocationA, warehouseLocationB)
      })

      sorted = sortedGroups.flat()
    } else {
      sorted = selections.sort((left, right) => {
        const typedLeft = left as { _id: string; fill: Fill; order: Order }
        const typedRight = right as { _id: string; fill: Fill; order: Order }

        const leftIsBatch = (typedLeft.order?.rxFillRequests?.length ?? 0) > 1
        const rightIsBatch = (typedRight.order?.rxFillRequests?.length ?? 0) > 1

        if (leftIsBatch || rightIsBatch) {
          if (leftIsBatch && !rightIsBatch) return -1
          if (!leftIsBatch && rightIsBatch) return 1

          const leftPatient = typedLeft.order.rxFillRequests.find(req => req.fillId === typedLeft.fill?._id)?.patient
          const rightPatient = typedRight.order.rxFillRequests.find(req => req.fillId === typedRight.fill?._id)?.patient
          return leftPatient?.lastName.localeCompare(rightPatient?.lastName as string) ?? 0
        } else {
          const leftName = typedLeft.fill?.dispensed?.name ?? ''
          const rightName = typedRight.fill?.dispensed?.name ?? ''
          return leftName.localeCompare(rightName)
        }
      })
    }
  }

  const nonBatchOrderFillIds: Fill['_id'][] = sorted
    .filter(sel => (sel as Fill).order?.rxFillRequests?.length === 1)
    .map(sel => sel._id)
  const fillIds = sorted.map(fill => fill._id)
  const fills = sorted.map(fill => {
    return {
      fillId: fill._id,
      orderId: (fill as Fill)?.order?._id || '',
    }
  })
  const automationFills = sorted.map(selection => {
    const { _id, fill, order } = selection as { _id: string; fill?: Fill; order?: Order }
    return {
      fillId: _id,
      orderId: order?._id || '',
      name: fill?.dispensed?.name,
    }
  })
  const orders = sorted.map(order => {
    return (order as Order)?._id
  })
  const hasReprints = selections.some(
    item => ((item as { _id: string; fill: Fill; order: Order })?.fill?.labelsPrinted || 0) > 0,
  )
  const ActionableOrderSort = () => {
    const hasOtcOnlyOrOtcRxOnly = otcOnlyControl || otcRxOnlyControl
    const showOrderSort =
      fulfillmentQueue?.name === FulfillmentQueueName.Fill ||
      (fulfillmentQueue?.name === FulfillmentQueueName.Packing && hasOtcOnlyOrOtcRxOnly)

    if (!showOrderSort) {
      return null
    }

    let sortOptions = [{ label: 'Age', value: 'age' }]
    if (fulfillmentQueue?.name === FulfillmentQueueName.Fill) {
      sortOptions = [
        ...sortOptions,
        { label: 'Drug Name', value: 'drugName' },
        { label: 'Quantity', value: 'dispensedQty' },
      ]
    }
    if (fulfillmentQueue?.name === FulfillmentQueueName.Packing) {
      sortOptions = [
        ...sortOptions,
        { label: 'OTC Quantity', value: 'otcQuantity' },
        { label: 'OTC Name', value: 'otcName' },
      ]
    }

    return <OrderSortDropdown options={sortOptions} />
  }

  const ActionablePrint = () => {
    const currentAutobot: AutobotType = queryMap.autobot || AutobotType.mini
    const autobotShowPrint = currentAutobot === AutobotType.noStatus ? false : autobotMap[currentAutobot].showPrint
    const showPrint =
      fulfillmentQueue?.name === FulfillmentQueueName.Fill ||
      (fulfillmentQueue?.name === FulfillmentQueueName.Automation && autobotShowPrint)

    if (!showPrint) {
      return null
    }

    return <PrintActionButtons hasReprints={hasReprints} fills={fills} />
  }

  const ActionablePickSlipPrint = () => {
    const isPackingQueue = fulfillmentQueue?.name === FulfillmentQueueName.Packing

    if (!isPackingQueue) {
      return <></>
    }

    if (!otcOnlyControl && !otcRxOnlyControl) {
      return <></>
    }

    return <PrintPickSlipActionButtons orders={orders} />
  }

  const StartWorkPV1 = () => {
    const showStartWork = fulfillmentQueue?.name === FulfillmentQueueName.PV1
    if (!showStartWork) {
      return null
    }

    return (
      <ActionButton
        id="StartWork"
        icon={<StartWorkIcon />}
        label={'Start work'}
        hotKey={'F12'}
        onClick={() => startPV1Work()}
      />
    )
  }

  const StartWorkTriage = () => {
    const showStartWork = fulfillmentQueue?.name === FulfillmentQueueName.Triage
    if (!showStartWork) {
      return null
    }

    return (
      <ActionButton
        id="StartWork"
        icon={<StartWorkIcon />}
        label={'Start work'}
        hotKey={'F12'}
        onClick={() => startTriageWork()}
      />
    )
  }

  const StartWorkAdjudication = () => {
    const showStartWork = fulfillmentQueue?.name === FulfillmentQueueName.Adjudication

    if (!showStartWork) {
      return null
    }

    return (
      <ActionButton
        icon={<StartWorkIcon />}
        label={'Start work'}
        onClick={() => startAdjudicationWork()}
        hotKey={'F12'}
      />
    )
  }

  const StartWorkOtcOnly = () => {
    const showStartWork =
      fulfillmentQueue?.name === FulfillmentQueueName.Packing && QueryToolkit.StringBoolean(queryMap.otcOnly)

    if (!showStartWork) {
      return null
    }

    return (
      <ActionButton
        id="StartWork"
        icon={<StartWorkIcon />}
        label={'Start work'}
        hotKey={'F12'}
        onClick={() => startOtcOnlyWork()}
      />
    )
  }

  const CreateCopayRequests = () => {
    const showCreateCopay = fulfillmentQueue?.name === FulfillmentQueueName.Copay

    if (!showCreateCopay) {
      return null
    }

    return (
      <AuthLimited roles={[UserRoles.Developer]}>
        <StyledBasicActionButton
          label={loadingCreateCopayRequest ? 'Generating...' : 'Generate Fixture Copay'}
          icon={
            <IconWrapper>
              <PlusIcon fill={'black'} />
            </IconWrapper>
          }
          onClick={() => {
            showModal(() => <CreateCopayRequestModal />)
          }}
        />
      </AuthLimited>
    )
  }

  const AutomationActionButtons = () => {
    const isAutomationQueue = fulfillmentQueue?.name === FulfillmentQueueName.Automation

    if (!isAutomationQueue) {
      return <></>
    }

    return (
      <>
        {autobot === AutobotType.deferredAutomation && <SendToAutomationButton fills={fills} deferred />}
        <SendToManualButton fillIds={nonBatchOrderFillIds} />
        <BulkSyncAutomationButton fillIds={fillIds} />
      </>
    )
  }

  const FillQueueSendToAutomation = () => {
    const isFillQueue = fulfillmentQueue?.name === FulfillmentQueueName.Fill

    if (!isFillQueue) {
      return <></>
    }

    return (
      <AuthLimited roles={[UserRoles.LeadPharmacist, UserRoles.Pharmacist, UserRoles.Technician]}>
        <SendToAutomationButton
          fills={automationFills}
          allowBatch
          onCompleted={() => {
            initSelections([])
            void client.refetchQueries({ include: ['getFulfillmentOrders'] })
          }}
        />
      </AuthLimited>
    )
  }

  return (
    <span>
      <ActionButtonContainer>
        <ActionableOrderSort />
        {fulfillmentQueue?.name && (
          <>
            <OrderSlaFilterDropdown />
            <PageSizeDropDown />
          </>
        )}
        <ActionablePrint />
        <FillQueueSendToAutomation />
        <StartWorkPV1 />
        <StartWorkTriage />
        <StartWorkOtcOnly />
        <CreateCopayRequests />
        <StartWorkAdjudication />
        <AutomationActionButtons />
        <ActionablePickSlipPrint />
      </ActionButtonContainer>
    </span>
  )
}

const BulkSyncAutomationButton = ({ fillIds }: { fillIds: string[] }): JSX.Element => {
  const showErrorToast = useErrorToast()
  const showInfoToast = useInfoToast()
  const { initSelections, selections } = useSelectionContext()
  const [bulkSyncAutomationStatus, { loading }] = useMutation<{
    bulkSyncAutomationStatus: [{ result?: string; error?: string }]
  }>(BULK_SYNC_AUTOMATION_STATUS, {
    onCompleted() {
      showInfoToast('Messages successfully sent to Parata machine')
      initSelections([])
    },
    onError(err) {
      showErrorToast(`Failed to bulk sync automation status with error: ${err}.`)
      initSelections([])
    },
    refetchQueries: ['getOrdersCountByAutomationStatus'],
  })
  return (
    <AuthLimited roles={[UserRoles.Pharmacist, UserRoles.Technician]}>
      <ActionButton
        disabled={loading || selections.length === 0}
        icon={
          loading ? (
            <IconWrapper>
              <LoadingDots fill={bodyPrimaryColor} />
            </IconWrapper>
          ) : (
            <IconWrapper>
              <ReverseIcon fill={bodyPrimaryColor} />
            </IconWrapper>
          )
        }
        onClick={() => {
          void bulkSyncAutomationStatus({ variables: { fills: fillIds } })
        }}
        label="Resync Status"
      />
    </AuthLimited>
  )
}

export const PrintActionButtons: FC<{
  hasReprints: boolean
  fills: { fillId: string; orderId: string }[]
  orderId?: string
}> = ({ hasReprints, fills }) => {
  const { showModal } = useModalContext()
  const { selections, initSelections, totalSelectableItems } = useSelectionContext()

  const showPrintModal = () => (
    <PrintModal
      title={`${hasReprints ? 'Reprint' : 'Print'} label`}
      fills={fills}
      isReprint={hasReprints}
      confirmationCallback={() => initSelections([])}
    />
  )

  return (
    <WrapButton variant="primary-outline" onClick={() => showModal(showPrintModal)} disabled={selections.length === 0}>
      <IconWrapper>
        <PrintIcon />
      </IconWrapper>
      {`Print ${fills.length && fills.length !== totalSelectableItems ? 'Selected' : 'All'}`}
    </WrapButton>
  )
}

export const PrintPickSlipActionButtons: FC<{
  orders: string[]
}> = ({ orders }) => {
  const { showModal } = useModalContext()
  const { selections, initSelections, totalSelectableItems } = useSelectionContext()

  const showPrintPickSlipModal = () => (
    <PrintPickSlipModal confirmationCallback={() => initSelections([])} orderIDs={orders} />
  )

  return (
    <WrapButton
      variant="primary-outline"
      onClick={() => showModal(showPrintPickSlipModal)}
      disabled={selections.length === 0}
    >
      <IconWrapper>
        <PrintIcon />
      </IconWrapper>
      {`Print ${orders.length && orders.length !== totalSelectableItems ? 'Selected' : 'All'}`}
    </WrapButton>
  )
}

const SendToManualButton = ({ fillIds }: { fillIds: string[] }): JSX.Element => {
  const { showModal } = useModalContext()
  const showErrorToast = useErrorToast()
  const showSuccessToast = useSuccessToast()
  const [bulkSendToManual, { loading }] = useMutation(BULK_SEND_TO_MANUAL, {
    onCompleted() {
      showSuccessToast(`Successfully sent to manual.`)
    },
    onError(err) {
      showErrorToast(err.message)
    },
  })

  return (
    <AuthLimited roles={[UserRoles.Admin]}>
      <ActionButton
        icon={
          loading ? (
            <IconWrapper>
              <LoadingDots fill={bodyPrimaryColor} />
            </IconWrapper>
          ) : (
            <IconWrapper>
              <RouteIcon fill={bodyPrimaryColor} />
            </IconWrapper>
          )
        }
        label={'Send to Manual'}
        disabled={!fillIds || fillIds.length === 0 || loading}
        onClick={() =>
          showModal(() => (
            <SendToManualModal
              isBulk={true}
              onConfirm={async () => {
                await bulkSendToManual({
                  variables: { fillIds },
                })
              }}
            />
          ))
        }
      />
    </AuthLimited>
  )
}

const StartWorkIcon = styled.span`
  border: solid ${bodyPrimaryColor};
  border-width: 0 3px 3px 0;
  display: inline-block;
  padding: 3px;
  transform: rotate(-45deg);
`

export default ActionButtons
